import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import style from "./ServiceOccurenceInfoTab.module.css";
import i18n from "../../Services/i18n";
import PictureUploadField from "../PictureUploadField/PictureUploadField";
import DateTimePicker from "../DateTimePicker/DateTimePicker";
import DateTimeService from "../../Services/datetimeService";
import { usePrivileges } from "../../Context/PrivilegeContext";
import PrivilegeValidationService from "../../Services/privilegeValidationService";
import RichEditor from "../RichEditor/RichEditor";
import RichEditorHelper from "../../Tools/RichEditorHelper";

const ServiceOccurenceInfoTab = forwardRef((props, ref) => {
  const { serviceOccurence, client, horse, services, onDirtyChange } = props;

  const pictureUploadFieldRef = useRef(null);
  const { privileges } = usePrivileges();

  const [selectedService, setSelectedService] = useState(
    serviceOccurence?.service ?? (services && services.length > 0 ? services[0] : null),
  );
  const [mainNote, setMainNote] = useState(serviceOccurence?.mainNote ?? "");
  const [dateServiceOcc, setDateServiceOcc] = useState(
    serviceOccurence?.dateServiceOccurence
      ? new Date(serviceOccurence.dateServiceOccurence)
      : new Date(),
  );

  const computeIsDirty = () => {
    const original = serviceOccurence;

    if (!original) return true;

    const pictureFieldData = pictureUploadFieldRef.current?.getData?.() ?? {};

    return (
      original.mainNote !== mainNote ||
      RichEditorHelper.normalizeHtmlForCompare(original.mainNote) !==
        RichEditorHelper.normalizeHtmlForCompare(mainNote) ||
      original.service?.externalId !== selectedService?.externalId ||
      DateTimeService.toLocalISOString(new Date(original.dateServiceOccurence)) !==
        DateTimeService.toLocalISOString(dateServiceOcc) ||
      original.defaultImageId !== pictureFieldData.fileId
    );
  };

  useEffect(() => {
    if (!serviceOccurence) return;

    // Ignore les objets partiels / transitoires
    const hasEnoughData =
      "mainNote" in serviceOccurence ||
      "service" in serviceOccurence ||
      "dateServiceOccurence" in serviceOccurence;

    if (!hasEnoughData) return;

    setSelectedService(
      serviceOccurence.service ?? (services && services.length > 0 ? services[0] : null),
    );
    setMainNote(serviceOccurence.mainNote ?? "");
    setDateServiceOcc(
      serviceOccurence.dateServiceOccurence
        ? new Date(serviceOccurence.dateServiceOccurence)
        : new Date(),
    );
    props.onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOccurence]);

  useEffect(() => {
    const dirty = computeIsDirty();
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainNote, selectedService, dateServiceOcc, onDirtyChange]);

  useImperativeHandle(ref, () => ({
    getFormData() {
      const pictureFieldData = pictureUploadFieldRef.current.getData();
      return {
        clientExternalId: client.externalId,
        horseExternalId: horse?.externalId,
        mainNote: mainNote,
        defaultImageId: pictureFieldData.fileId,
        defaultImageIsFromDefaultSource: pictureFieldData.isFromDefault,
        serviceExternalId: selectedService.externalId,
        dateServiceOccurence: DateTimeService.toLocalISOString(dateServiceOcc),
      };
    },
    isDirty: computeIsDirty,
    reset(newSO) {
      setSelectedService(newSO.service ?? (services && services.length > 0 ? services[0] : null));
      setMainNote(newSO.mainNote ?? "");
      setDateServiceOcc(
        newSO.dateServiceOccurence ? new Date(newSO.dateServiceOccurence) : new Date(),
      );
      onDirtyChange?.(false);
    },
  }));

  const handleServiceChange = (e) => {
    setSelectedService(services.find((x) => x.externalId === e.target.value));
  };

  const getImageList = async () => {
    if (
      !PrivilegeValidationService.ServiceOccurenceCanAddMedia(
        await privileges(),
        serviceOccurence.medias?.length,
      )
    ) {
      return serviceOccurence.medias;
    } else {
      return null;
    }
  };

  const getDaysAgo = (date) => {
    if (!date) return null;

    const serviceDate = new Date(date);
    const today = new Date();

    serviceDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = today - serviceDate;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };
  const daysAgo = getDaysAgo(dateServiceOcc);

  return (
    <div className={style.container}>
      <div className={style.quickFormLeft}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            paddingBottom: "10px",
          }}>
          <label style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
            {i18n.t("ServiceOccurenceForm.FieldServiceDate")}
          </label>

          <div style={{ width: "40%" }}>
            <DateTimePicker
              name="dateServiceOcc"
              value={dateServiceOcc}
              onChange={(d) => setDateServiceOcc(d)}
              readOnly={false}
            />
          </div>

          {daysAgo !== null && (
            <span style={{ whiteSpace: "nowrap" }}>
              {i18n.t("ServiceOccurenceForm.DaysAgo", { count: daysAgo })}
            </span>
          )}
        </div>

        <label className={style.quickFormLabel}>
          {i18n.t("ServiceOccurenceForm.FieldClient")}
          <input
            readOnly
            type="text"
            className={`${style.quickFormInput} ${style.readOnlyInput}`}
            value={client.cardName}
          />
        </label>

        {horse && horse.externalId !== -1 && (
          <label className={style.quickFormLabel}>
            {i18n.t("ServiceOccurenceForm.FieldHorse")}
            <input
              readOnly
              type="text"
              className={`${style.quickFormInput} ${style.readOnlyInput}`}
              value={horse.cardName}
            />
          </label>
        )}

        <label className={style.quickFormLabel}>
          {i18n.t("ServiceOccurenceForm.FieldService")}
          <select
            value={selectedService?.externalId}
            onChange={handleServiceChange}
            className={style.quickFormSelect}>
            {services &&
              services.map((service) => (
                <option key={service.externalId} value={service.externalId}>
                  {service.title}{" "}
                  {service.isDeleted ? i18n.t("ServiceOccurenceForm.UserServiceDeleted") : ""}
                </option>
              ))}
          </select>
        </label>

        <div className={style.quickFormLabel}>
          <div className={style.quickFormLabelText}>
            {i18n.t("ServiceOccurenceForm.FieldMainNote")}
          </div>

          <RichEditor
            controlled
            value={mainNote}
            onChange={(html) => setMainNote(html)}
            placeholder=""
            height={300}
            showEmoji
            showTextColor
            showFontSize
            showBold
            showItalic
            showUnderline
            showStrikethrough
          />
        </div>
      </div>

      <div className={style.quickFormRight}>
        <PictureUploadField
          key={serviceOccurence?.externalId ?? "new"}
          initFileId={serviceOccurence?.defaultImageId}
          ref={pictureUploadFieldRef}
          horseThumbnail={horse?.defaultImage}
          horseThumbnailfileId={horse?.defaultImageId}
          clientThumbnail={client.defaultImage}
          clientThumbnailFileId={client.defaultImageId}
          serviceThumbnail={selectedService?.defaultImage}
          serviceThumbnailFileId={selectedService?.defaultImageId}
          image={serviceOccurence?.defaultImage}
          restrictedImageList={getImageList}
        />
      </div>
    </div>
  );
});

export default ServiceOccurenceInfoTab;
